import { useQueries, useQuery } from '@tanstack/react-query';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import SearchService, { TypeaheadType } from '~/services/SearchService';

export const RELEVANT_TYPEAHEAD_TYPES: Array<TypeaheadType> = [
  'airport',
  'bedbank_property_unique_stays',
  'bedbank_property',
  'city',
  'colloquial_area',
  'continent',
  'country',
  'high_level_region',
  'le_property_unique_stays',
  'le_property',
  'le_property_rental',
  'metro_station',
  'multi_city_vicinity',
  'neighborhood',
  'point_of_interest',
  'province_state',
  'train_station',
  'le_experience',
  'channel_experience',
  'car_hire_location',
];

export type Destination = {
  fk?: string;
  primary_text?: string;
  secondary_text?: string;
  type?: string;
  id?: string;
};

interface UseDestinationsTypeaheadParams {
  query: string;
  enabled?: boolean;
}

const STALE_TIME = 10 * 60 * 1000;

export function useDestinationsTypeahead({ query, enabled = true }: UseDestinationsTypeaheadParams) {
  const brand = useCurrentBrand();
  return useQuery({
    queryKey: ['destinations', 'typeahead', query, brand],
    queryFn: async () => {
      if (query.length < 2) {
        return { result: [] };
      }
      const result = await SearchService.typeahead(query, {
        type: RELEVANT_TYPEAHEAD_TYPES,
        brand: brand.toLowerCase(),
        region: 'AU',
        limit: 20,
        priority: 'current',
      });
      return result;
    },
    enabled: enabled && query.length >= 2,
    staleTime: STALE_TIME,
  });
}

interface UsePlacesParams {
  placeIds: Array<string>;
  enabled?: boolean;
}

export function usePlaces({ placeIds, enabled = true }: UsePlacesParams) {
  return useQueries({
    queries: placeIds.map((placeId) => ({
      queryKey: ['destinations', 'place', placeId],
      queryFn: async () => {
        const result = await SearchService.getPlace(placeId);
        return {
          placeId,
          result,
        };
      },
      enabled: enabled && !!placeId,
      staleTime: STALE_TIME,
    })),
  });
}
